// src/pages/learn/scripts.tsx
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  BookOpen,
  Wrench,
  Zap,
  Star,
  CheckCircle,
} from "lucide-react";

const ScriptsEntryPage = () => {
  const ScriptCard = ({
    title,
    price,
    description,
    features,
    link,
    isExternal = false,
  }: {
    title: string;
    price: string;
    description: string;
    features: string[];
    link: string;
    isExternal?: boolean;
  }) => (
    <Card className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-all duration-300 h-full flex flex-col">
      <CardHeader className="text-center pb-4">
        <div className="h-12 w-12 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center mx-auto mb-3">
          <Wrench className="h-6 w-6 text-blue-600 dark:text-blue-400" />
        </div>
        <CardTitle className="text-xl font-bold mb-2">{title}</CardTitle>
        <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
          {price}
        </div>
      </CardHeader>

      <CardContent className="flex-1 flex flex-col">
        <p className="text-gray-600 dark:text-gray-300 mb-4 text-center">
          {description}
        </p>

        <div className="mb-6 flex-1">
          <ul className="space-y-2">
            {features.map((feature, index) => (
              <li key={index} className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {feature}
                </span>
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-auto">
          {isExternal ? (
            <a
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block w-full"
            >
              <Button className="w-full bg-blue-500 hover:bg-blue-600 text-white">
                Get Script <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </a>
          ) : (
            <Link to={link} className="inline-block w-full">
              <Button className="w-full bg-blue-500 hover:bg-blue-600 text-white">
                Get Script <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          )}
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-4 py-12">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
            Choose Your Scripts Path
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto mb-8">
            Whether you want to learn the basics, grab proven scripts, or buy
            professional tools - we've got you covered.
          </p>
        </div>

        {/* Three Main Options */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
          {/* Option 1: Do the Scary */}
          <Link to="/scary" className="block h-full">
            <Card className="bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-800/20 border-orange-200 dark:border-orange-700 hover:shadow-lg transition-all duration-300 h-full flex flex-col cursor-pointer">
              <CardHeader className="text-center pb-6">
                <div className="h-16 w-16 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center mx-auto mb-4">
                  <Zap className="h-8 w-8 text-white" />
                </div>
                <CardTitle className="text-2xl font-bold text-orange-700 dark:text-orange-400 mb-2">
                  Learn the Basics
                </CardTitle>
                <div className="flex justify-center">
                  <Badge className="bg-orange-500 text-white text-xs px-2 py-1">
                    Free Challenge
                  </Badge>
                </div>
              </CardHeader>

              <CardContent className="flex-1 flex flex-col">
                <p className="text-lg text-gray-700 dark:text-gray-300 mb-6 text-center">
                  New to Google Ads Scripts? Start with our "Scripts Aren't
                  Scary" challenge. Learn the fundamentals step-by-step.
                </p>

                <div className="mb-6 flex-1">
                  <ul className="space-y-3">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-orange-500" />
                      <span>Interactive learning experience</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-orange-500" />
                      <span>Step-by-step guidance</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-orange-500" />
                      <span>Build confidence with scripts</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-orange-500" />
                      <span>Perfect for beginners</span>
                    </li>
                  </ul>
                </div>

                <div className="mt-auto">
                  <Button className="w-full bg-orange-500 hover:bg-orange-600 text-white">
                    Start Challenge <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </Link>

          {/* Option 2: Get 4Cs Scripts */}
          <Link to="/4cs" className="block h-full">
            <Card className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 border-purple-200 dark:border-purple-700 hover:shadow-lg transition-all duration-300 h-full flex flex-col cursor-pointer">
              <CardHeader className="text-center pb-6">
                <div className="h-16 w-16 rounded-full bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center mx-auto mb-4">
                  <BookOpen className="h-8 w-8 text-white" />
                </div>
                <CardTitle className="text-2xl font-bold text-purple-700 dark:text-purple-400 mb-2">
                  Get Free Scripts
                </CardTitle>
                <div className="flex justify-center">
                  <Badge className="bg-purple-500 text-white text-xs px-2 py-1">
                    4Cs Framework
                  </Badge>
                </div>
              </CardHeader>

              <CardContent className="flex-1 flex flex-col">
                <p className="text-lg text-gray-700 dark:text-gray-300 mb-6 text-center">
                  Access proven scripts following our 4Cs framework: Check,
                  Chart, Change, and Cognition.
                </p>

                <div className="mb-6 flex-1">
                  <ul className="space-y-3">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-purple-500" />
                      <span>Check: Zero impressions script</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-purple-500" />
                      <span>Chart: Export to Google Sheets</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-purple-500" />
                      <span>Change: Pause overspend script</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-purple-500" />
                      <span>Cognition: AI insights script</span>
                    </li>
                  </ul>
                </div>

                <div className="mt-auto">
                  <Button className="w-full bg-purple-500 hover:bg-purple-600 text-white">
                    Get Scripts <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </Link>

          {/* Option 3: Buy Professional Scripts */}
          <Link to="/buy" className="block h-full">
            <Card className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 border-blue-200 dark:border-blue-700 hover:shadow-lg transition-all duration-300 h-full flex flex-col cursor-pointer">
              <CardHeader className="text-center pb-6">
                <div className="h-16 w-16 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center mx-auto mb-4">
                  <Star className="h-8 w-8 text-white" />
                </div>
                <CardTitle className="text-2xl font-bold text-blue-700 dark:text-blue-400 mb-2">
                  Buy Pro Scripts
                </CardTitle>
                <div className="flex justify-center">
                  <Badge className="bg-blue-500 text-white text-xs px-2 py-1">
                    Professional Tools
                  </Badge>
                </div>
              </CardHeader>

              <CardContent className="flex-1 flex flex-col">
                <p className="text-lg text-gray-700 dark:text-gray-300 mb-6 text-center">
                  Ready-to-use professional scripts for Performance Max and
                  negative keyword management.
                </p>

                <div className="mb-6 flex-1">
                  <ul className="space-y-3">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-blue-500" />
                      <span>MCC PMax Script ($399)</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-blue-500" />
                      <span>Single PMax Script ($199)</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-blue-500" />
                      <span>Negative Keywords ($109)</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-blue-500" />
                      <span>Professional support included</span>
                    </li>
                  </ul>
                </div>

                <div className="mt-auto">
                  <Button className="w-full bg-blue-500 hover:bg-blue-600 text-white">
                    View Scripts <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>

        {/* Professional Scripts Details Section */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-8 text-gray-800 dark:text-gray-200">
            Professional Scripts Available
          </h2>
          <p className="text-center text-gray-600 dark:text-gray-400 mb-12 max-w-2xl mx-auto">
            These battle-tested scripts are used by thousands of Google Ads
            professionals worldwide.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <ScriptCard
              title="MCC PMax Script"
              price="$399"
              description="Performance Max insights for unlimited accounts"
              features={[
                "Create PMax sheets for all accounts",
                "Includes negative keyword script",
                "Lifetime updates",
                "Priority support",
              ]}
              link="https://mikerhodes.circle.so/checkout/latest-mcc-script"
              isExternal={true}
            />

            <ScriptCard
              title="Single Account PMax Script"
              price="$199"
              description="Performance Max insights for one account"
              features={[
                "Create PMax insights in seconds",
                "Free training videos",
                "Support & updates included",
                "Easy to install",
              ]}
              link="/pmax"
              isExternal={false}
            />

            <ScriptCard
              title="Negative Keyword Script"
              price="$109"
              description="Automated negative keyword management"
              features={[
                "Works with Search campaigns",
                "Visualise your data in new ways",
                "Automated reports",
                "Time-saving automation",
              ]}
              link="https://mikerhodes.circle.so/checkout/negative-keyword-script"
              isExternal={true}
            />
          </div>
        </div>

        {/* Call to Action */}
        <div className="text-center bg-gradient-to-r from-blue-600 to-purple-600 text-white p-8 rounded-2xl">
          <h3 className="text-2xl font-bold mb-4">Not sure where to start?</h3>
          <p className="text-lg mb-6 opacity-90">
            Begin with our free challenge to build your confidence, then explore
            our professional scripts.
          </p>
          <Link to="/scary">
            <Button
              size="lg"
              className="bg-white text-blue-600 hover:bg-gray-100"
            >
              Start Free Challenge <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ScriptsEntryPage;
